import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { IChapter, IPosition } from '../pages/Assistance.tsx';
import { applyPatchedPositionsToChapterMap } from './useChapterManagement.ts';

interface ListOfServicesStateContextValue {
    chapterMap: Map<string, IChapter> | null;
    setChapterMap: React.Dispatch<React.SetStateAction<Map<string, IChapter> | null>>;
    applyPositionPatch: (positions: IPosition[]) => void;
    loadedChapters: Set<string>;
    markChapterAsLoaded: (chapterNumber: string) => void;
    isChapterLoaded: (chapterNumber: string) => boolean;
    resetLoadedChapters: () => void;
}

const noOp = () => {};

const ListOfServicesStateContext = createContext<ListOfServicesStateContextValue>({
    chapterMap: null,
    setChapterMap: noOp,
    applyPositionPatch: noOp,
    loadedChapters: new Set(),
    markChapterAsLoaded: noOp,
    isChapterLoaded: () => false,
    resetLoadedChapters: noOp,
});

export const ListOfServicesStateProvider = ({ children }: { children: React.ReactNode }) => {
    const [chapterMap, setChapterMap] = useState<Map<string, IChapter> | null>(null);
    const [loadedChapters, setLoadedChapters] = useState<Set<string>>(new Set());
    const loadedChaptersRef = useRef<Set<string>>(loadedChapters);
    loadedChaptersRef.current = loadedChapters;

    const applyPositionPatch = useCallback((positions: IPosition[]) => {
        setChapterMap((prevMap) => {
            if (!prevMap) return prevMap;
            return applyPatchedPositionsToChapterMap(prevMap, positions);
        });
    }, []);

    const markChapterAsLoaded = useCallback((chapterNumber: string) => {
        setLoadedChapters((prev) => new Set([...prev, chapterNumber]));
    }, []);

    // Use ref-based lookup so this callback is stable and doesn't change when loadedChapters changes.
    // This prevents cascading re-renders of the entire context consumer tree.
    const isChapterLoaded = useCallback((chapterNumber: string) => {
        return loadedChaptersRef.current.has(chapterNumber);
    }, []);

    const resetLoadedChapters = useCallback(() => {
        setLoadedChapters(new Set());
    }, []);

    const value = useMemo(
        () => ({
            chapterMap,
            setChapterMap,
            applyPositionPatch,
            loadedChapters,
            markChapterAsLoaded,
            isChapterLoaded,
            resetLoadedChapters,
        }),
        [chapterMap, loadedChapters]
    );

    return <ListOfServicesStateContext.Provider value={value}>{children}</ListOfServicesStateContext.Provider>;
};

export const useListOfServicesState = () => useContext(ListOfServicesStateContext);
